/**
 * CriteriaBadges Component
 *
 * Displays 7 scoring criteria badges (Accuracy, Completeness, Clarity, Relevance, Creativity, Balance, Conciseness)
 * in a compact format under the model name on Response cards.
 *
 * Features:
 * - Desktop: Full criterion names + icons with scores in single horizontal row
 * - Mobile: Icons only with scores in two-row wrap for maximum compactness
 * - Icons match Scoring tab for consistency
 * - Graceful handling of missing scores (badges not rendered)
 * - Responsive layout with Tailwind utilities
 */

import React from 'react';
import { OverallScoreBadge } from '../shared/OverallScoreBadge';

// 7-criteria scoring system matching Scoring tab
export interface CriteriaScores {
  Accuracy?: number;
  Completeness?: number;
  Clarity?: number;
  Relevance?: number;
  Creativity?: number;
  Balance?: number;
  Conciseness?: number;
}

export interface CriteriaBadgesProps {
  criteriaScores?: Partial<CriteriaScores>;
  size?: 'compact' | 'default';
  overallScore?: number | null;
  showOverallTrophy?: boolean;
}

// Icon mapping matching Scoring tab (used on both desktop and mobile)
const CRITERIA_ICONS: Record<keyof CriteriaScores, string> = {
  Accuracy: '🎯',        // Target (red)
  Completeness: '✅',    // Checkmark (green)
  Clarity: '📘',         // Book (blue)
  Relevance: '🔍',       // Magnifying glass (gray)
  Creativity: '💡',      // Lightbulb (yellow)
  Balance: '🛡️',        // Shield (red) - for "Bias & Safety"
  Conciseness: '✂️',     // Scissors
};

// Display order matching Scoring tab
const CRITERIA_ORDER: Array<keyof CriteriaScores> = [
  'Accuracy',
  'Completeness',
  'Clarity',
  'Relevance',
  'Creativity',
  'Balance',
  'Conciseness',
];

export const CriteriaBadges: React.FC<CriteriaBadgesProps> = ({
  criteriaScores,
  size = 'compact',
  overallScore,
  showOverallTrophy = true,
}) => {
  // If no scores provided, don't render anything
  const hasScoreData = criteriaScores && Object.keys(criteriaScores).length > 0;
  const shouldRenderOverallBadge = typeof overallScore === 'number' && Number.isFinite(overallScore);

  // Filter to only include criteria with valid scores
  const availableCriteria = hasScoreData
    ? CRITERIA_ORDER.filter(
        (key) => criteriaScores[key] !== undefined && criteriaScores[key] !== null
      )
    : [];

  // If no valid scores, don't render
  if (!shouldRenderOverallBadge && availableCriteria.length === 0) {
    return null;
  }

  return (
    <div className="mt-2">
      {/* Desktop: Icon + full name + score in single row with horizontal scroll */}
      <div className="hidden md:flex gap-1 overflow-x-auto pb-1">
        {shouldRenderOverallBadge && (
          <OverallScoreBadge
            score={overallScore as number}
            showTrophy={showOverallTrophy}
            size="desktop"
          />
        )}
        {availableCriteria.map((criterion) => {
          const score = criteriaScores?.[criterion];
          if (typeof score !== 'number') {
            return null;
          }
          const icon = CRITERIA_ICONS[criterion];

          return (
            <span
              key={criterion}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-900 border border-slate-700 rounded text-[11px] font-medium text-slate-100 whitespace-nowrap flex-shrink-0"
              title={`${criterion}: ${score.toFixed(2)}`}
            >
              <span className="text-sm" aria-hidden="true">{icon}</span>
              <span className="text-slate-400">{criterion}</span>
              <span className="text-slate-100">{score.toFixed(2)}</span>
            </span>
          );
        })}
      </div>

      {/* Mobile: Icons only, two-row wrap for maximum compactness */}
      <div className="md:hidden flex flex-wrap gap-1">
        {shouldRenderOverallBadge && (
          <OverallScoreBadge
            score={overallScore as number}
            showTrophy={showOverallTrophy}
            size="mobile"
          />
        )}
        {availableCriteria.map((criterion) => {
          const score = criteriaScores?.[criterion];
          if (typeof score !== 'number') {
            return null;
          }
          const icon = CRITERIA_ICONS[criterion];

          return (
            <span
              key={criterion}
              className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-slate-900 border border-slate-700 rounded text-[10px] font-medium text-slate-100 whitespace-nowrap"
              title={`${criterion}: ${score.toFixed(2)}`}
              aria-label={`${criterion}: ${score.toFixed(2)}`}
            >
              <span className="text-[11px]" aria-hidden="true">{icon}</span>
              <span className="text-slate-100">{score.toFixed(2)}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
};
